import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { StyleSheet, Text } from 'react-native';
import Config from 'react-native-config';
import { AppButton } from '../../components/AppButton';
import { AppCard } from '../../components/AppCard';
import { AppLoader } from '../../components/AppLoader';
import { AppScreen } from '../../components/AppScreen';
import { ErrorState } from '../../components/ErrorState';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAppColors } from '../../context/PreferencesContext';
import { getHealth, type HealthResult } from '../../services/healthService';
import { formatApiError } from '../../utils/formatApiError';

type State = {
  loading: boolean;
  result?: HealthResult;
  error?: ReturnType<typeof formatApiError>;
};

export function ApiStatusScreen() {
  const palette = useAppColors();
  const [state, setState] = useState<State>({ loading: true });
  const checkHealth = useCallback(async () => {
    setState({ loading: true });
    try {
      setState({ loading: false, result: await getHealth() });
    } catch (error) {
      setState({ loading: false, error: formatApiError(error) });
    }
  }, []);

  useEffect(() => {
    checkHealth();
  }, [checkHealth]);

  const themed = useMemo(
    () => ({
      title: { ...typography.screenTitle, color: palette.textPrimary },
      subtitle: {
        ...typography.body,
        color: palette.textSecondary,
        marginBottom: spacing.xl,
      },
      label: { ...typography.caption, color: palette.textMuted },
      host: { ...typography.body, color: palette.textPrimary },
      status: { ...typography.sectionTitle },
      connected: { color: palette.success },
      unavailable: { color: palette.error },
      body: { ...typography.body, color: palette.textSecondary },
      request: { ...typography.caption, color: palette.textMuted },
    }),
    [palette],
  );

  return (
    <AppScreen scroll>
      <Text style={themed.title}>API Status</Text>
      <Text style={themed.subtitle}>
        Check the connection to the configured backend.
      </Text>
      <AppCard style={styles.card}>
        <Text style={themed.label}>Configured API host</Text>
        <Text style={themed.host}>
          {Config.API_BASE_URL || 'Not configured'}
        </Text>
      </AppCard>
      {state.loading ? <AppLoader message="Checking API connection" /> : null}
      {state.result ? (
        <AppCard style={styles.card}>
          <Text
            style={[
              themed.status,
              state.result.healthy ? themed.connected : themed.unavailable,
            ]}
          >
            {state.result.healthy ? 'Connected' : 'Unavailable'}
          </Text>
          <Text style={themed.body}>{state.result.message}</Text>
          {state.result.requestId ? (
            <Text style={themed.request}>
              Request ID: {state.result.requestId}
            </Text>
          ) : null}
          <AppButton title="Retry" onPress={checkHealth} variant="secondary" />
        </AppCard>
      ) : null}
      {state.error ? (
        <ErrorState
          message={state.error.message}
          requestId={state.error.requestId}
          onRetry={checkHealth}
        />
      ) : null}
    </AppScreen>
  );
}

const styles = StyleSheet.create({
  card: { gap: spacing.md, marginBottom: spacing.lg },
});
